import { cloneDeep, findLastIndex, isEqual, omit, uniq } from 'lodash';

import { FeatureChanges, Plan } from '@core/api';

import {
  CreateOperation,
  DeleteOperation,
  DuplicateOperation,
  FeatureRelatedOperation,
  HistoryOperation,
  LinkOperation,
  Operation,
  OrderOperation,
  PatchOperation,
  PatchOperationChunk,
  RelinkOperation,
  UnlinkOperation,
} from '../../models/operations.model';
import { GeoJSONFeature } from '../models/feature.model';

export function toCreateOperation(feature: GeoJSONFeature): CreateOperation {
  return {
    type: 'create',
    feature,
  };
}

export function toPatchOperation(feature: GeoJSON.Feature, all: GeoJSON.Feature[], chunk?: PatchOperationChunk): PatchOperation {
  const before = all.find((f) => f.id === feature.id);
  const geometry = chunk !== 'properties' ? toGeometryChange(before, feature) : undefined;
  const properties = toPropertiesChanges(before, feature);

  return {
    type: 'patch',
    featureId: feature.id as string | number,
    geometry,
    properties,
    chunk,
  };
}

export function toDuplicateOperation(feature: GeoJSON.Feature, changes: FeatureChanges): DuplicateOperation {
  return {
    type: 'duplicate',
    feature,
    changes,
  };
}

export function toDeleteOperation(feature: GeoJSON.Feature): DeleteOperation {
  return {
    type: 'delete',
    feature,
  };
}

export function toOrderOperation(features: (string | number)[], plan: Plan): OrderOperation {
  return {
    type: 'order',
    features,
    before: plan.features,
  };
}

export function toLinkOperation(featureId: string | number, propertyId: string): LinkOperation {
  return {
    type: 'link',
    featureId,
    propertyId,
  };
}

export function toUnlinkOperation(featureId: string | number, propertyId: string): UnlinkOperation {
  return {
    type: 'unlink',
    featureId,
    propertyId,
  };
}

export function toRelinkOperation(featureId: string | number, propertyId: string, before: string): RelinkOperation {
  return {
    type: 'relink',
    featureId,
    propertyId,
    before,
  };
}

export function toHistoryOperations(operations: Operation[], state: HistoryOperation[], skip?: boolean): HistoryOperation[] {
  const relevants = state?.filter((hop) => !hop.status);

  if (!relevants?.length) {
    return operations.map((op) => ({ ...op, skip }));
  }

  const result = [...relevants];

  operations.forEach((op) => {
    if (isOrderOperation(op)) {
      result.push(skip ? { ...op, skip } : op);
      return;
    }

    const operationFeatureId = getOperationFeatureId(op);
    const index = findLastIndex(result, (rel) => {
      if (!isFeatureRelatedOperation(rel) || !isFeatureRelatedOperation(op) || operationFeatureId === undefined) {
        return false;
      }

      return getOperationFeatureId(rel) === operationFeatureId;
    });

    const relevant = index > -1 ? result[index] : undefined;

    if (relevant) {
      if (relevant.type === 'create' && op.type === 'patch') {
        const nextFeature = applyPatchOperationOnFeature(relevant.feature, op, 'to');
        result[index] = skip ? { ...relevant, feature: nextFeature, skip } : { ...relevant, feature: nextFeature };
      } else if (relevant.type === 'patch' && op.type === 'patch') {
        const mergedPatch = mergePatchOperations(relevant, op);

        if (mergedPatch) {
          result[index] = skip ? { ...mergedPatch, skip } : mergedPatch;
        } else {
          result.splice(index, 1);
        }
      } else {
        result.push(skip ? { ...op, skip } : op);
      }
    } else {
      result.push(skip ? { ...op, skip } : op);
    }
  });

  return result.filter((op) => !!op);
}

export function toReversibleOperations(hops: HistoryOperation[], state: Operation[][]): Operation[][] {
  const operations = hops.filter((hop) => !hop.skip).map((hop) => omit(hop, 'status', 'skip') as Operation);
  return operations.length ? (state ? [...state, operations] : [operations]) : state;
}

export function convertToUndoOperations(operations: Operation[]): Operation[] {
  return operations.map((operation) => {
    if (operation.type === 'create' || operation.type === 'duplicate') {
      return {
        ...operation,
        type: 'delete',
      };
    }

    if (operation.type === 'delete') {
      return {
        ...operation,
        type: 'restore',
      };
    }

    if (operation.type === 'patch') {
      return {
        ...operation,
        geometry: operation.geometry ? { from: cloneDeep(operation.geometry.to), to: cloneDeep(operation.geometry.from) } : undefined,
        properties: operation.properties?.map((property) => ({
          key: property.key,
          change: {
            from: cloneDeep(property.change.to),
            to: cloneDeep(property.change.from),
          },
        })),
      };
    }

    if (operation.type === 'order') {
      return {
        ...operation,
        features: operation.before,
        before: operation.features,
      };
    }

    if (operation.type === 'link') {
      return {
        ...operation,
        type: 'unlink',
      };
    }

    if (operation.type === 'unlink') {
      return {
        ...operation,
        type: 'link',
      };
    }

    if (operation.type === 'relink') {
      return {
        ...operation,
        type: 'link',
        propertyId: operation.before,
        before: operation.propertyId,
      };
    }

    return operation;
  });
}

export function convertToRedoOperations(operations: Operation[]): Operation[] {
  return operations.map((operation) => {
    if (operation.type === 'create' || operation.type === 'duplicate') {
      return {
        ...operation,
        type: 'restore',
      };
    }

    return operation;
  });
}

export function applyOperationsOnFeatures(operations: Operation[], features: GeoJSONFeature[]) {
  const result = [...features];

  operations.forEach((op) => {
    if (op.type === 'create' || op.type === 'duplicate') {
      result.push(op.feature);
    }

    if (op.type === 'restore') {
      const index = result.findIndex((f) => f.id === op.feature.id);

      if (index > -1) {
        result[index] = { ...result[index], ...op.feature, deleted: false };
      } else {
        result.push({ ...op.feature, deleted: false });
      }
    }

    if (op.type === 'patch') {
      const index = result.findIndex((f) => f.id === op.featureId);

      if (index > -1) {
        result[index] = applyPatchOperationOnFeature(result[index], op, 'to');
      }
    }

    if (op.type === 'delete') {
      const index = result.findIndex((f) => f.id === op.feature.id);

      if (index > -1) {
        result[index] = { ...result[index], deleted: true };
      }
    }

    if (op.type === 'link' || op.type === 'relink') {
      const index = result.findIndex((f) => f.id === op.featureId);

      if (index > -1) {
        result[index] = { ...result[index], propertyId: op.propertyId };
      }
    }

    if (op.type === 'unlink') {
      const index = result.findIndex((f) => f.id === op.featureId);

      if (index > -1) {
        result[index] = { ...result[index], propertyId: undefined };
      }
    }
  });

  return result;
}

export function revertOperationsOnFeatures(operations: Operation[], features: GeoJSONFeature[]) {
  const result = [...features];

  operations.forEach((op) => {
    if (op.type === 'link') {
      const index = result.findIndex((f) => f.id === op.featureId);

      if (index > -1) {
        result[index] = { ...result[index], propertyId: undefined };
      }
    }

    if (op.type === 'relink') {
      const index = result.findIndex((f) => f.id === op.featureId);

      if (index > -1) {
        result[index] = { ...result[index], propertyId: op.before };
      }
    }

    if (op.type === 'unlink') {
      const index = result.findIndex((f) => f.id === op.featureId);

      if (index > -1) {
        result[index] = { ...result[index], propertyId: op.propertyId };
      }
    }
  });

  return result;
}

export function applyOperationsOnOrder(operations: Operation[], features: (string | number)[]) {
  let result = [...features];

  operations.forEach((op) => {
    if (op.type === 'order') {
      result = [...op.features];
    }
  });

  return result;
}

function isFeatureRelatedOperation(operation: Operation): operation is FeatureRelatedOperation {
  return ['create', 'duplicate', 'patch', 'delete', 'restore'].includes(operation.type);
}

function isOrderOperation(operation: Operation): operation is OrderOperation {
  return operation.type === 'order';
}

function toGeometryChange(before: GeoJSON.Feature | undefined, feature: GeoJSON.Feature) {
  if (!before) {
    return undefined;
  }

  if (isEqual(before.geometry, feature.geometry)) {
    return undefined;
  }

  return {
    from: cloneDeep(before.geometry),
    to: cloneDeep(feature.geometry),
  };
}

function toPropertiesChanges(before: GeoJSON.Feature | undefined, feature: GeoJSON.Feature) {
  if (!before) {
    return undefined;
  }

  const beforeProperties = before.properties || {};
  const currentProperties = feature.properties || {};
  const keys = uniq([...Object.keys(beforeProperties), ...Object.keys(currentProperties)]);

  const changes = keys
    .filter((key) => !isEqual(beforeProperties[key], currentProperties[key]))
    .map((key) => ({
      key,
      change: {
        from: cloneDeep(beforeProperties[key]),
        to: cloneDeep(currentProperties[key]),
      },
    }));

  return changes.length ? changes : undefined;
}

function applyPatchOperationOnFeature(feature: GeoJSON.Feature, operation: PatchOperation, direction: 'from' | 'to'): GeoJSON.Feature {
  let nextFeature = { ...feature };

  if (operation.geometry) {
    nextFeature = {
      ...nextFeature,
      geometry: operation.geometry[direction] as GeoJSON.Geometry,
    };
  }

  if (operation.properties?.length) {
    const baseProperties = (nextFeature.properties || {}) as GeoJSON.GeoJsonProperties;
    const properties = operation.properties.reduce(
      (acc, property) => {
        acc[property.key] = property.change[direction];
        return acc;
      },
      { ...baseProperties }
    );

    nextFeature = {
      ...nextFeature,
      properties,
    };
  }

  return nextFeature;
}

function mergePatchOperations(previous: PatchOperation, incoming: PatchOperation): PatchOperation | undefined {
  const geometry = incoming.geometry
    ? {
        from: cloneDeep(previous.geometry ? previous.geometry.from : incoming.geometry.from),
        to: cloneDeep(incoming.geometry.to),
      }
    : previous.geometry;

  const mergedByKey = new Map<string, { from: unknown; to: unknown }>();

  previous.properties?.forEach((property) => {
    mergedByKey.set(property.key, { from: cloneDeep(property.change.from), to: cloneDeep(property.change.to) });
  });

  incoming.properties?.forEach((property) => {
    const existing = mergedByKey.get(property.key);
    if (existing) {
      mergedByKey.set(property.key, { from: cloneDeep(existing.from), to: cloneDeep(property.change.to) });
    } else {
      mergedByKey.set(property.key, { from: cloneDeep(property.change.from), to: cloneDeep(property.change.to) });
    }
  });

  const properties = Array.from(mergedByKey.entries())
    .map(([key, change]) => ({ key, change }))
    .filter((property) => !isEqual(property.change.from, property.change.to));

  const normalizedGeometry = geometry && !isEqual(geometry.from, geometry.to) ? geometry : undefined;
  const normalizedProperties = properties.length ? properties : undefined;

  if (!normalizedGeometry && !normalizedProperties) {
    return undefined;
  }

  return {
    ...previous,
    geometry: normalizedGeometry,
    properties: normalizedProperties,
    chunk: undefined,
  };
}

export function getOperationFeatureId(operation: Operation): string | number | undefined {
  switch (operation.type) {
    case 'create':
    case 'duplicate':
    case 'delete':
    case 'restore':
      return operation.feature.id as string | number;
    case 'patch':
      return operation.featureId;
    case 'link':
    case 'unlink':
    case 'relink':
      return operation.featureId;
    default:
      return undefined;
  }
}
